import uuidv4 from 'uuid/v4';

import { ISiteSettingsForServerAdmin } from '../../../settings/types/ISite';
import { ISiteCommentingSettings } from '../../../settings/types/ISiteCommentingSettings';
import { IRemoteAgentSiteSettings, ISiteAdminSettings, ISiteSettingsForSiteAdmin, ISiteSubscriptionSettings } from '../../../settings/types/ISiteSettingsForSiteAdmin';
import { SiteSettingServerAdminTelemetryEvent, SiteSettingSiteAdminTelemetryEvent, TelemetryEvents } from '../TelemetryEvents';

export interface SettingUUID {
    uuid: string | undefined;
    isSiteAdmin: boolean | undefined;
}

let siteSettingForSiteAdminUUID: SettingUUID  = {uuid: undefined, isSiteAdmin: undefined};

export const buildSiteSettingServerAdminTelemetryEvent = (
    site: ISiteSettingsForServerAdmin, master: ISiteSettingsForServerAdmin,eventId: string): SiteSettingServerAdminTelemetryEvent => ({
        type: TelemetryEvents.SiteSettingServerAdmin,
        adminCount_new: site.adminCount,
        adminCount_old: master.adminCount,
        authoringEnabled_new: site.settings.authoringEnabled,
        authoringEnabled_old: master.settings.authoringEnabled,
        availability_new: site.availability,
        availability_old: master.availability,
        cacheWarmupEnabled_new: site.settings.cacheWarmupEnabled,
        cacheWarmupEnabled_old: master.settings.cacheWarmupEnabled,
        catalogingOnSiteEnabled_new:site.settings.catalogingOnSiteEnabled,
        catalogingOnSiteEnabled_old: master.settings.catalogingOnSiteEnabled,
        contentVersionLimit_new: site.settings.contentVersionLimit,
        contentVersionLimit_old: master.settings.contentVersionLimit,
        contentVersionLimitEnabled_new: site.settings.contentVersionLimitEnabled,
        contentVersionLimitEnabled_old:master.settings.contentVersionLimitEnabled,
        currentLoggedInUserCount_new: site.currentLoggedInUserCount,
        currentLoggedInUserCount_old: master.currentLoggedInUserCount,
        customSubscriptionEmailEnabled_new: site.settings.customSubscriptionEmailEnabled,
        customSubscriptionEmailEnabled_old: master.settings.customSubscriptionEmailEnabled,
        customSubscriptionFooterEnabled_new: site.settings.customSubscriptionFooterEnabled,
        customSubscriptionFooterEnabled_old: master.settings.customSubscriptionFooterEnabled,
        flowsOnSiteEnabled_new: site.settings.flowsOnSiteEnabled,
        flowsOnSiteEnabled_old: master.settings.flowsOnSiteEnabled,
        guestAccessEnabled_new: site.settings.guestAccessEnabled,
        guestAccessEnabled_old: master.settings.guestAccessEnabled,
        licensedUserCount_new: site.licensedUserCount,
        licensedUserCount_old:master.licensedUserCount,
        metricsEnabled_new: site.settings.metricsEnabled,
        metricsEnabled_old: master.settings.metricsEnabled,
        siteAdminsCanAddUsers_new: site.settings.siteAdminsCanAddUsers,
        siteAdminsCanAddUsers_old: master.settings.siteAdminsCanAddUsers,
        storageQuotaBytes_new: site.settings.storageQuotaBytes,
        storageQuotaBytes_old: master.settings.storageQuotaBytes,
        storageQuotaEnabled_new:site.settings.storageQuotaEnabled,
        storageQuotaEnabled_old: master.settings.storageQuotaEnabled,
        storageQuotaPercentUsed_new: site.storageQuotaPercentUsed,
        storageQuotaPercentUsed_old: master.storageQuotaPercentUsed,
        storageUsedBytes_new: site.storageUsedBytes,
        storageUsedBytes_old: master.storageUsedBytes,
        userCount_new: site.userCount,
        userCount_old: master.userCount,
        userQuota_new: site.settings.userQuota,
        userQuota_old: master.settings.userQuota,
        userQuotaMode_new: site.settings.userQuotaMode,
        userQuotaMode_old: master.settings.userQuotaMode,
        versionHistoryEnabled_new:site.settings.versionHistoryEnabled,
        versionHistoryEnabled_old:master.settings.versionHistoryEnabled,
        runNowEnabled_new: site.settings.runNowEnabled,
        runNowEnabled_old: master.settings.runNowEnabled,
        eventid: eventId,
        siteId: site.id,
        tierCreatorCapacity_new: site.settings.tierCreatorCapacity,
        tierCreatorCapacity_old: master.settings.tierCreatorCapacity,
        tierExplorerCapacity_new: site.settings.tierExplorerCapacity,
        tierExplorerCapacity_old: master.settings.tierExplorerCapacity,
        tierViewerCapacity_new: site.settings.tierViewerCapacity,
        tierViewerCapacity_old: master.settings.tierViewerCapacity,
        creatorCount_new: site.creatorCount,
        creatorCount_old: master.creatorCount,
        explorerCount_new: site.explorerCount,
        explorerCount_old: master.explorerCount,
        viewerCount_new: site.viewerCount,
        viewerCount_old: master.viewerCount
});

const buildNewSubscriptionSettings = (newSubscriptionSettings: ISiteSubscriptionSettings): {} => {
    if(newSubscriptionSettings) {
        return {
            allowSubscriptionAttachments_new: newSubscriptionSettings.allowSubscriptionAttachments,
            customScheduleSubscriptionForSiteEnabled_new: newSubscriptionSettings.customScheduleSubscriptionForSiteEnabled,
            serverAllowSubscriptionAttachments_new: newSubscriptionSettings.serverAllowSubscriptionAttachments,
            subscribeOthersEnabled_new: newSubscriptionSettings.subscribeOthersEnabled,
            subscriptionsEnabled_new : newSubscriptionSettings.subscriptionsEnabled,
        };
    }
    return {};
};

const builOldSubscriptionSettings = (oldSubscriptionSettings: ISiteSubscriptionSettings): {} => {
    if(oldSubscriptionSettings) {
        return {
            allowSubscriptionAttachments_old: oldSubscriptionSettings.allowSubscriptionAttachments,
            customScheduleSubscriptionForSiteEnabled_old: oldSubscriptionSettings.customScheduleSubscriptionForSiteEnabled,
            serverAllowSubscriptionAttachments_old: oldSubscriptionSettings.serverAllowSubscriptionAttachments,
            subscribeOthersEnabled_old: oldSubscriptionSettings.subscribeOthersEnabled,
            subscriptionsEnabled_old : oldSubscriptionSettings.subscriptionsEnabled,
        };
    }
    return {};
};

const buildNewSiteCommentingSettings = (newSiteCommentingSettings: ISiteCommentingSettings): {} => {
    if(newSiteCommentingSettings) {
        return {
            commentingEnabled_new: newSiteCommentingSettings.commentingEnabled,
            commentingMentionsEnabled_new: newSiteCommentingSettings.commentingMentionsEnabled,
        };
    }
    return {};
};

const buildOldSiteCommentingSettings = (oldSiteCommentingSettings: ISiteCommentingSettings): {} => {
    if(oldSiteCommentingSettings) {
        return {
            commentingEnabled_old: oldSiteCommentingSettings.commentingEnabled,
            commentingMentionsEnabled_old: oldSiteCommentingSettings.commentingMentionsEnabled,
        };
    }
    return {};
};

const buildNewNotificationForClientNotRunningEnabled = (newRemoteAgentSettings: IRemoteAgentSiteSettings): {} => {
    if(newRemoteAgentSettings) {
        return {
            notificationForClientNotRunningEnabled_new: newRemoteAgentSettings.notificationForClientNotRunningEnabled,
        };
    }
    return {};
};

const buildOldNotificationForClientNotRunningEnabled = (oldRemoteAgentSettings: IRemoteAgentSiteSettings): {} => {
    if(oldRemoteAgentSettings) {
        return {
            notificationForClientNotRunningEnabled_old: oldRemoteAgentSettings.notificationForClientNotRunningEnabled,
        };
    }
    return {};
};

export const buildSiteSettingSiteAdminTelemetryEvent = (
    site: ISiteSettingsForSiteAdmin, master: ISiteSettingsForSiteAdmin, eventId: string): SiteSettingSiteAdminTelemetryEvent => {
        // remoteAgentSettings and siteCommentingSettings could be undefined inside ISiteAdminSettings
        // make sure return undefined when accessing properties inside remoteAgentSettings and siteCommentingSettings when they are undefined

        const newSiteAdminSettings = site.siteAdminSettings as ISiteAdminSettings;
        const oldSiteAdminSettings = master.siteAdminSettings as ISiteAdminSettings;

        return {
            ...buildNewSubscriptionSettings(site.subscriptionSettings!),
            ...builOldSubscriptionSettings(master.subscriptionSettings!),
            ...buildNewSiteCommentingSettings(newSiteAdminSettings.siteCommentingSettings),
            ...buildOldSiteCommentingSettings(oldSiteAdminSettings.siteCommentingSettings),
            ...buildNewNotificationForClientNotRunningEnabled(newSiteAdminSettings.remoteAgentSettings),
            ...buildOldNotificationForClientNotRunningEnabled(oldSiteAdminSettings.remoteAgentSettings),
            type: TelemetryEvents.SiteSettingSiteAdmin,
            addSupportUserEnabled_new: newSiteAdminSettings.addSupportUserEnabled,
            addSupportUserEnabled_old: oldSiteAdminSettings.addSupportUserEnabled,
            alternativeFederationEngineEnabled_new: newSiteAdminSettings.alternativeFederationEngineEnabled,
            alternativeFederationEngineEnabled_old: oldSiteAdminSettings.alternativeFederationEngineEnabled,
            askDataMode_new: newSiteAdminSettings.askDataMode,
            askDataMode_old: oldSiteAdminSettings.askDataMode,
            dataAlertsEnabled_new: newSiteAdminSettings.dataAlertsEnabled,
            dataAlertsEnabled_old: oldSiteAdminSettings.dataAlertsEnabled,
            derivedPermissionsEnabled_new: newSiteAdminSettings.derivedPermissionsEnabled,
            derivedPermissionsEnabled_old: oldSiteAdminSettings.derivedPermissionsEnabled,
            extractEncryptionMode_new: newSiteAdminSettings.extractEncryptionMode,
            extractEncryptionMode_old: oldSiteAdminSettings.extractEncryptionMode,
            isAdminDataSourceEnabled_new: newSiteAdminSettings.isAdminDataSourceEnabled,
            isAdminDataSourceEnabled_old: oldSiteAdminSettings.isAdminDataSourceEnabled,
            namedSharingEnabled_new: newSiteAdminSettings.siteNamedSharingSettings.namedSharingEnabled,
            namedSharingEnabled_old: oldSiteAdminSettings.siteNamedSharingSettings.namedSharingEnabled,
            notificationEnabled_new: newSiteAdminSettings.notificationEnabled,
            notificationEnabled_old: oldSiteAdminSettings.notificationEnabled,
            personalSpaceEnabled_new: newSiteAdminSettings.sitePersonalSpaceSettings.personalSpaceEnabled,
            personalSpaceEnabled_old: oldSiteAdminSettings.sitePersonalSpaceSettings.personalSpaceEnabled,
            personalSpaceStorageQuotaSet_new: newSiteAdminSettings.sitePersonalSpaceSettings.personalSpaceStorageQuotaSet,
            personalSpaceStorageQuotaSet_old: oldSiteAdminSettings.sitePersonalSpaceSettings.personalSpaceStorageQuotaSet,
            personalSpaceStorageQuota_new: newSiteAdminSettings.sitePersonalSpaceSettings.personalSpaceStorageQuota,
            personalSpaceStorageQuota_old: oldSiteAdminSettings.sitePersonalSpaceSettings.personalSpaceStorageQuota,
            refreshTokenEnabled_new: newSiteAdminSettings.refreshTokenEnabled,
            refreshTokenEnabled_old: oldSiteAdminSettings.refreshTokenEnabled,
            remoteQueryingEnabled_new: newSiteAdminSettings.remoteQueryingEnabled,
            remoteQueryingEnabled_old: oldSiteAdminSettings.remoteQueryingEnabled,
            requestAccessEnabled_new: newSiteAdminSettings.requestAccessEnabled,
            requestAccessEnabled_old: oldSiteAdminSettings.requestAccessEnabled,
            sheetImageEnabled_new: newSiteAdminSettings.sheetImageEnabled,
            sheetImageEnabled_old: oldSiteAdminSettings.sheetImageEnabled,
            siteInviteEmailEnabled_new: newSiteAdminSettings.siteInviteEmailEnabled,
            siteInviteEmailEnabled_old: oldSiteAdminSettings.siteInviteEmailEnabled,
            userVisibility_new: newSiteAdminSettings.userVisibility,
            userVisibility_old: oldSiteAdminSettings.userVisibility,
            vizRecommendationsEnabled_new: newSiteAdminSettings.vizRecommendationsEnabled,
            vizRecommendationsEnabled_old: oldSiteAdminSettings.vizRecommendationsEnabled,
            vizRecUsernameVisibilityEnabled_new: newSiteAdminSettings.vizRecUsernameVisibilityEnabled,
            vizRecUsernameVisibilityEnabled_old: oldSiteAdminSettings.vizRecUsernameVisibilityEnabled,
            eventid: eventId,
            siteId: site.siteId,
    };
};

// this method is used by buildSiteSettingSiteAdminTelemetryEvent and buildSiteSettingServerAdminTelemetryEvent
// need to make sure they have the same UUID when save button is cliced
export const getSiteSettingUUID = (isSiteAdmin: boolean): string => {
    // for online site admin setting change will only call updateSiteSettingsForSiteAdmin 
    // we should only share uuid if one save action trigger both updateSiteSettingsForSiteAdmin  and 
    // updateSiteSettingsForServerAdmin
    if(siteSettingForSiteAdminUUID.uuid && siteSettingForSiteAdminUUID.isSiteAdmin !== isSiteAdmin) {
        const uuid = siteSettingForSiteAdminUUID.uuid;
        siteSettingForSiteAdminUUID = {uuid: undefined, isSiteAdmin: undefined};
        return uuid;
    }

    siteSettingForSiteAdminUUID = {uuid: uuidv4(), isSiteAdmin: isSiteAdmin};
    return siteSettingForSiteAdminUUID.uuid;
};
